import { Localization } from "@viamap/viamap2-common";
import { useContext, useState } from "react";
import { GlassButton } from "src/components/GlassButtons";
import { ADModalBody, ADModalFooter, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { FRPContext, FRPElement } from "./FRPState";
import { SheetFunc } from "src/managers/SheetFunc";
import { ExportData } from "src/managers/ExportData";
import { FRPFunc } from "./FRPFunc";
import { GlassInputControl } from "src/components/GlassInput";
import jsPDF from "jspdf";

const SELECTED_OPTION_VALUE = "__selected__";

export function FRPExportToExcel(props: {
   managedWindow: ManagedWindow,
   disabled?: boolean,
}) {
   const { state: frpState, dispatch: _frpDispatch } = useContext(FRPContext);
   const { dispatch } = useWindowContext();
   const [selectedElementId, setSelectedElementId] = useState<string>("");


   function ExportFileToExcel() {



      try {
         const selectedIdsFromState = frpState.selectedElementIds || [];
         const useSelectedList = selectedElementId === SELECTED_OPTION_VALUE && selectedIdsFromState.length > 0;

         let rootElements = FRPFunc.determineAllRootElements(frpState);

         if (useSelectedList) {
            rootElements = selectedIdsFromState;
         } else if (selectedElementId) {
            rootElements = [selectedElementId];
         }

         const maxDepth = findMaxDepth(frpState, rootElements);
         const hierarchicalData = buildHierarchicalData(frpState, rootElements);


         const sheetsData = [
            {
               sheetName: frpState.selectedProject?.name || Localization.getText("Unknown Project"),
               headers: generateHeaders(maxDepth),
               rows: hierarchicalData
            },
            {
               sheetName: Localization.getText("Project Summary"),
               headers: [Localization.getText("Metric"), Localization.getText("Value")],
               rows: buildProjectSummary(
                  frpState,
                  rootElements,
                  useSelectedList ? selectedIdsFromState : (selectedElementId ? [selectedElementId] : [])
               )
            }
         ];

         const workbook = SheetFunc.createWorkBookFromJSON(
            {
               Title: `FRP Export - ${frpState.selectedProject?.name || Localization.getText("Unknown Project")}`,
               Author: "Mapit",
               CreatedDate: new Date()
            },
            sheetsData
         );

         const blob = SheetFunc.createBlob(workbook);
         const fileName = `FRP_Export_${frpState.selectedProject?.name?.replace(/\s+/g, '_') || Localization.getText("Project")}_${new Date().toISOString().split('T')[0]}.xlsx`;
         ExportData.downloadBlob(fileName, blob);
         //dispatch(closeWindow(props.managedWindow.id));

      } catch (error) {
         console.error("Error exporting to Excel:", error);
      }
   }









   function buildHierarchicalData(frpState: any, rootElements?: string[]): any[] {
      if (!rootElements) {
         rootElements = FRPFunc.determineAllRootElements(frpState);
      }
      const result: any[] = [];

      const maxDepth = findMaxDepth(frpState, rootElements);

      function getStatusName(element: FRPElement, statusOptions: any[]): string {
         const statusResult = FRPFunc.determineNestedChildrenStatusWithSource(element, frpState);

         if (!statusResult.status) return Localization.getText("No Status");

         const statusOption = statusOptions?.find(option => option.id === statusResult.status);
         if (statusResult.elementId && statusResult.elementId !== element.id) {
            return statusOption?.val + " " + "(" + Localization.getText("Inherited") + ")" || statusResult.status + " " + "(" + Localization.getText("Inherited") + ")";
         } else {
            return statusOption?.val || statusResult.status;
         }
      }
      function getTagsName(tags: string[], tagOptions: any[]): string {
         return tags.map(tag => tagOptions?.find(option => option.id === tag)?.val || tag).join(", ");
      }

      function getExpectedStartDate(element: FRPElement): string {
         const expectedStartDate = FRPFunc.determineParentAttribute(element, frpState, "expectedStartDate");
         if (expectedStartDate?.elementId && expectedStartDate.elementId !== element.id) {
            return expectedStartDate.value + " " + "(" + Localization.getText("Inherited") + ")";
         } else {
            return expectedStartDate?.value || "";
         }
      }
      function getExpectedEndDate(element: FRPElement): string {
         const expectedEndDate = FRPFunc.determineParentAttribute(element, frpState, "expectedEndDate");
         if (expectedEndDate?.elementId && expectedEndDate.elementId !== element.id) {
            return expectedEndDate.value + " " + "(" + Localization.getText("Inherited") + ")";
         } else {
            return expectedEndDate?.value || "";
         }
      }

      function processElement(elementId: string, level: number = 0, parentPath: string[] = []): void {
         const element = frpState.elements[elementId];
         if (!element) return;

         const children = frpState.childrenLookupTable[elementId] || [];


         const rowData: any = {};

         for (let i = 0; i < maxDepth; i++) {
            const levelKey = Localization.getText("Layer") + " " + (i + 1);
            if (i === level) {
               rowData[levelKey] = element.name || element.id;
            } else {
               rowData[levelKey] = "";
            }
         }




         rowData[Localization.getText("Status")] = getStatusName(element, frpState.statusOptions);
         rowData[Localization.getText("Tags")] = getTagsName(element.tags, frpState.tagOptions);
         rowData[Localization.getText("Expected Start")] = getExpectedStartDate(element);
         rowData[Localization.getText("Expected End")] = getExpectedEndDate(element);
         rowData[Localization.getText("Description")] = element.description;
         rowData[Localization.getText("%Complete")] = FRPFunc.getElementProgressRecursive(elementId, 0, frpState).status + "%";

         result.push(rowData);

         if (result.length > 1) {
            const currentRow = result[result.length - 1];
            const previousRow = result[result.length - 2];

            for (let i = 0; i < maxDepth; i++) {
               const levelKey = Localization.getText("Layer") + " " + (i + 1);
               if (currentRow[levelKey] === previousRow[levelKey] && currentRow[levelKey] !== "") {
                  currentRow[levelKey] = "";
               }
            }
         }

         children.forEach(childId => {
            processElement(childId, level + 1, [...parentPath, elementId]);
         });
      }

      rootElements.forEach(rootId => processElement(rootId, 0));
      return result;
   }

   function generateHeaders(maxDepth: number): string[] {
      const levelHeaders: string[] = [];
      for (let i = 1; i <= maxDepth; i++) {
         levelHeaders.push(Localization.getText("Layer") + " " + i);
      }

      return [
         ...levelHeaders,
         Localization.getText("Status"),
         Localization.getText("%Complete"),
         Localization.getText("Tags"),
         Localization.getText("Expected Start"),
         Localization.getText("Expected End"),
         Localization.getText("Beskrivelse"),

      ];
   }



   function findMaxDepth(frpState: any, rootElements: string[]): number {
      let maxDepth = 0;

      function getDepth(elementId: string, currentDepth: number = 0): number {
         const children = frpState.childrenLookupTable[elementId] || [];
         if (children.length === 0) return currentDepth;

         let maxChildDepth = currentDepth;
         children.forEach(childId => {
            const childDepth = getDepth(childId, currentDepth + 1);
            maxChildDepth = Math.max(maxChildDepth, childDepth);
         });
         return maxChildDepth;
      }

      rootElements.forEach(rootId => {
         maxDepth = Math.max(maxDepth, getDepth(rootId));
      });

      return maxDepth + 1;
   }




   function buildProjectSummary(frpState: any, rootElements?: string[], filteredSelectionIds: string[] = []): any[] {
      if (!rootElements) {
         rootElements = FRPFunc.determineAllRootElements(frpState);
      }

      // Count total elements if filtering by selected element
      let totalElements = Object.keys(frpState.elements).length;
      if (filteredSelectionIds.length === 1) {
         const selectedOnlyId = filteredSelectionIds[0];
         const allChildrenIds = FRPFunc.allNestedChildrenIds(selectedOnlyId, frpState.childrenLookupTable);
         totalElements = allChildrenIds.length + 1;
      } else if (filteredSelectionIds.length > 1) {
         totalElements = filteredSelectionIds.length;
      }

      return [
         { [Localization.getText("Metric")]: Localization.getText("Project Name"), [Localization.getText("Value")]: frpState.selectedProject?.name || Localization.getText("Unknown") },
         { [Localization.getText("Metric")]: Localization.getText("Total Elements"), [Localization.getText("Value")]: totalElements },
         { [Localization.getText("Metric")]: Localization.getText("Root Elements"), [Localization.getText("Value")]: rootElements.length },
         { [Localization.getText("Metric")]: Localization.getText("Export Date"), [Localization.getText("Value")]: new Date().toLocaleDateString() }
      ];
   }

   function ExportFileToPDF() {
      try {
         const selectedIdsFromState = frpState.selectedElementIds || [];
         const useSelectedList = selectedElementId === SELECTED_OPTION_VALUE && selectedIdsFromState.length > 0;

         let rootElements = FRPFunc.determineAllRootElements(frpState);

         if (useSelectedList) {
            rootElements = selectedIdsFromState;
         } else if (selectedElementId) {
            rootElements = [selectedElementId];
         }

         const maxDepth = findMaxDepth(frpState, rootElements);
         const hierarchicalData = buildHierarchicalData(frpState, rootElements);
         const headers = generateHeaders(maxDepth);
         const summary = buildProjectSummary(
            frpState,
            rootElements,
            useSelectedList ? selectedIdsFromState : (selectedElementId ? [selectedElementId] : [])
         );

         const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
         const pageWidth = doc.internal.pageSize.width;
         const pageHeight = doc.internal.pageSize.height;
         const margin = 15;
         let yPos = margin;

         // Title
         doc.setFontSize(16);
         doc.text(Localization.getText("Export To Excel"), pageWidth / 2, yPos, { align: "center" });
         yPos += 10;

         // Introduction text
         doc.setFontSize(10);
         const introText = [
            Localization.getText("Project Name") + ": " + (frpState.selectedProject?.name || Localization.getText("Unknown Project")),
            Localization.getText("Export Date") + ": " + new Date().toLocaleDateString(),
            "",
            Localization.getText("This document contains the hierarchical overview of FRP elements.")
         ];

         introText.forEach(line => {
            if (yPos > pageHeight - 30) {
               doc.addPage();
               yPos = margin;
            }
            doc.text(line, margin, yPos);
            yPos += 6;
         });

         yPos += 5;

         // Table headers
         if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin;
         }

         doc.setFontSize(8);
         doc.setFont(undefined as any, "bold");
         const colWidths = calculateColumnWidths(headers, pageWidth - 2 * margin);
         let xPos = margin;

         headers.forEach((header, idx) => {
            doc.rect(xPos, yPos, colWidths[idx], 8, "S");
            doc.text(header, xPos + 2, yPos + 5);
            xPos += colWidths[idx];
         });
         yPos += 8;

         // Table rows
         doc.setFont(undefined as any, "normal");
         hierarchicalData.forEach((row) => {
            if (yPos > pageHeight - 15) {
               doc.addPage();
               yPos = margin;
               // Redraw headers on new page
               xPos = margin;
               doc.setFont(undefined as any, "bold");
               headers.forEach((header, idx) => {
                  doc.rect(xPos, yPos, colWidths[idx], 8, "S");
                  doc.text(header, xPos + 2, yPos + 5);
                  xPos += colWidths[idx];
               });
               yPos += 8;
               doc.setFont(undefined as any, "normal");
            }

            xPos = margin;
            headers.forEach((header, idx) => {
               const cellValue = String(row[header] || "");
               doc.rect(xPos, yPos, colWidths[idx], 6, "S");
               doc.text(cellValue.substring(0, 30), xPos + 2, yPos + 4);
               xPos += colWidths[idx];
            });
            yPos += 6;
         });

         // Summary section
         if (yPos > pageHeight - 40) {
            doc.addPage();
            yPos = margin;
         }

         yPos += 10;
         doc.setFontSize(12);
         doc.setFont(undefined as any, "bold");
         doc.text(Localization.getText("Project Summary"), margin, yPos);
         yPos += 8;

         doc.setFontSize(10);
         doc.setFont(undefined as any, "normal");
         summary.forEach(item => {
            if (yPos > pageHeight - 15) {
               doc.addPage();
               yPos = margin;
            }
            const metric = item[Localization.getText("Metric")];
            const value = item[Localization.getText("Value")];
            doc.text(metric + ": " + value, margin, yPos);
            yPos += 6;
         });

         const fileName = `FRP_Export_${frpState.selectedProject?.name?.replace(/\s+/g, '_') || Localization.getText("Project")}_${new Date().toISOString().split('T')[0]}.pdf`;
         doc.save(fileName);

      } catch (error) {
         console.error("Error exporting to PDF:", error);
      }
   }

   function calculateColumnWidths(headers: string[], totalWidth: number): number[] {
      const numCols = headers.length;
      const baseWidth = totalWidth / numCols;
      return headers.map(() => baseWidth);
   }




   return <AdvancedDragModal window={props.managedWindow} title={Localization.getText("Export To Excel")} onClose={() => { dispatch(closeWindow(props.managedWindow.id)) }}
      PosDefault={{ width: "400px", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
   >

      <ADModalInfo center={true}>
         {Localization.getText("Export To Excel:info")}
      </ADModalInfo>
      <ADModalBody>
         <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
               {Localization.getText("Export Element")}:
            </label>
            <GlassInputControl>
               <select
                  value={selectedElementId}
                  onChange={(e) => setSelectedElementId(e.target.value)}
                  style={{ width: "100%" }}
               >
                  <option value="">{Localization.getText("All Elements")}</option>
                  {(frpState.selectedElementIds?.length ?? 0) > 0 && (
                     <option value={SELECTED_OPTION_VALUE}>
                        {Localization.getText("Selected Elements")}
                     </option>
                  )}
                  {Object.keys(frpState.elements)
                     .filter(elementId => {
                        const children = frpState.childrenLookupTable[elementId] || [];
                        return children.length > 0;
                     })
                     .map(elementId => {
                        const element = frpState.elements[elementId];
                        return (
                           <option key={elementId} value={elementId}>
                              {element.name || element.id}
                           </option>
                        );
                     })}
               </select>
            </GlassInputControl>
         </div>
      </ADModalBody>

      <ADModalFooter>
         <GlassButton onClick={() => { dispatch(closeWindow(props.managedWindow.id)) }}>{Localization.getText("Cancel")}</GlassButton>
         <GlassButton disabled={props.disabled} onClick={ExportFileToPDF}>{Localization.getText("Export To PDF")}</GlassButton>
         <GlassButton disabled={props.disabled} onClick={ExportFileToExcel}>{Localization.getText("Export To Excel")}</GlassButton>
      </ADModalFooter>
   </AdvancedDragModal>
}